import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';

import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';

import { useStore } from '@common/components/StoreProvider';
import useTableModel from '@common/helpers/useTableModel';
import { ICuratorGroupModel } from '@common/models/CuratorGroups/CuratorGroup';
import CuratorGroups from '@common/models/CuratorGroups/CuratorGroups';

type ProgramGroupPayload = {
	id: number;
	channel_channels: boolean;
	group_channels: boolean;
	messaging_channels: boolean;
};

type FormValues = {
	program_groups: ProgramGroupPayload[];
	duplicate_to_email: boolean;
};

function AudienceTab() {
	const store = useStore();
	const { values, setFieldValue } = useFormikContext<FormValues>();

	const curatorGroups = useTableModel(CuratorGroups, { service: store.services.current }, [
		store.services.current,
	]);

	const selectionMap = useMemo(() => {
		const map = new Map<number, ProgramGroupPayload>();
		values.program_groups.forEach((pg) => map.set(pg.id, pg));
		return map;
	}, [values.program_groups]);

	const updateProgramGroup = useCallback(
		(next: ProgramGroupPayload) => {
			const filtered = values.program_groups.filter((pg) => pg.id !== next.id);

			const hasAny = next.channel_channels || next.group_channels || next.messaging_channels;

			setFieldValue('program_groups', hasAny ? [...filtered, next] : filtered);
		},
		[setFieldValue, values.program_groups],
	);

	const toggleRoot = useCallback(
		(pg: any, checked: boolean) => {
			const caps = pg.curator_program_group;

			updateProgramGroup({
				id: pg.id,
				channel_channels: !!caps?.has_channel_channels && checked,
				group_channels: !!caps?.has_group_channels && checked,
				messaging_channels: !!caps?.has_messaging_channels && checked,
			});
		},
		[updateProgramGroup],
	);

	const toggleInner = useCallback(
		(pg: any, key: keyof Omit<ProgramGroupPayload, 'id'>, checked: boolean) => {
			const prev = selectionMap.get(pg.id);

			updateProgramGroup({
				id: pg.id,
				channel_channels: prev?.channel_channels ?? false,
				group_channels: prev?.group_channels ?? false,
				messaging_channels: prev?.messaging_channels ?? false,
				[key]: checked,
			});
		},
		[selectionMap, updateProgramGroup],
	);

	return (
		<Box>
			{curatorGroups.data?.map((group: ICuratorGroupModel) => (
				<Box key={group.id} mb={3}>
					<Typography fontWeight={600} mb={1}>
						{group.name}
					</Typography>

					<Stack spacing={2}>
						{group.program_groups.map((pg: any) => {
							const caps = pg.curator_program_group;
							if (!caps) return null;

							const selected = selectionMap.get(pg.id);

							const rootChecked =
								(!caps.has_channel_channels || selected?.channel_channels) &&
								(!caps.has_group_channels || selected?.group_channels) &&
								(!caps.has_messaging_channels || selected?.messaging_channels);

							return (
								<Box key={pg.id} pl={1}>
									<FormControlLabel
										control={
											<Checkbox
												checked={!!rootChecked}
												onChange={(e) => toggleRoot(pg, e.target.checked)}
											/>
										}
										label={pg.name}
									/>

									<Stack pl={4}>
										{caps.has_group_channels && (
											<FormControlLabel
												control={
													<Checkbox
														checked={!!selected?.group_channels}
														onChange={(e) => toggleInner(pg, 'group_channels', e.target.checked)}
													/>
												}
												label="Group chats"
											/>
										)}

										{caps.has_channel_channels && (
											<FormControlLabel
												control={
													<Checkbox
														checked={!!selected?.channel_channels}
														onChange={(e) => toggleInner(pg, 'channel_channels', e.target.checked)}
													/>
												}
												label="Channels"
											/>
										)}

										{caps.has_messaging_channels && (
											<FormControlLabel
												control={
													<Checkbox
														checked={!!selected?.messaging_channels}
														onChange={(e) =>
															toggleInner(pg, 'messaging_channels', e.target.checked)}
													/>
												}
												label="Individual chats"
											/>
										)}
									</Stack>
								</Box>
							);
						})}
					</Stack>
				</Box>
			))}
		</Box>
	);
}

export default observer(AudienceTab);
